'use client'

import textPromoTitleL from '@fukku/styles/texts/textPromoTitleL.module.css'
import textPromoTitleM from '@fukku/styles/texts/textPromoTitleM.module.css'
import { cx } from 'classix'
import { ServerLabel } from 'labels/components/ServerLabel/ServerLabel'
import { Marquee } from 'landings/components/Marquee/Marquee'
import type { Cta, PointOfInterestImage } from 'landings/Home/Home.type'
import { type ReactNode, useRef } from 'react'
import { MarqueeProvider } from '../../../../../components/Marquee/MarqueeProvider'
import focusStyle from '../../Shared/A11y/TabFocus.module.css'
import { ButtonCtas } from '../../Shared/ctas/ButtonCtas/ButtonCtas'
import { useActiveLink } from '../../Shared/ctas/hooks/onActiveLink'
import {
	DEVICE_TYPE,
	HeroBannerImage,
} from '../components/HeroBannerImage/HeroBannerImage'
import { AnimationButton } from './AnimationButton'
import type { AnimationButtonTitles } from './HeroBannerPrimaryLines'
import styles from './HeroBannerPrimaryLines.module.css'

type HeroBannerLinesPrimaryComponentProps = {
	readonly image: PointOfInterestImage
	readonly ctas: Cta[]
	readonly isPrioritySlot: boolean | undefined
	readonly animationButtonTitles: AnimationButtonTitles
	readonly motionTitleLabel: string
}

const ASPECT_RATIO_SMALL_WIDTH = 5
const ASPECT_RATIO_SMALL_HEIGHT = 7
const ASPECT_RATIO_SMALL = ASPECT_RATIO_SMALL_WIDTH / ASPECT_RATIO_SMALL_HEIGHT

const ASPECT_RATIO_MEDIUM_HEIGHT = 600

const ASPECT_RATIO_LARGE_WIDTH = 7
const ASPECT_RATIO_LARGE_HEIGHT = 3
const ASPECT_RATIO_LARGE = ASPECT_RATIO_LARGE_WIDTH / ASPECT_RATIO_LARGE_HEIGHT

export function HeroBannerPrimaryLinesContent({
	image,
	ctas,
	isPrioritySlot,
	animationButtonTitles,
	motionTitleLabel,
}: HeroBannerLinesPrimaryComponentProps): ReactNode {
	const ref = useRef<HTMLDivElement>(null)
	const activeLink = useActiveLink(ref)

	return (
		<div className={styles.bannerWrapper} ref={ref}>
			<HeroBannerImage
				image={image}
				deviceHeightCalc={{
					[DEVICE_TYPE.Mobile]: (width) => width / ASPECT_RATIO_SMALL,
					[DEVICE_TYPE.Tablet]: () => ASPECT_RATIO_MEDIUM_HEIGHT,
					[DEVICE_TYPE.Desktop]: (width) => width / ASPECT_RATIO_LARGE,
				}}
				priority={isPrioritySlot}
			/>
			<MarqueeProvider>
				<Marquee className={styles.marquee}>
					<span className={cx(styles.title, textPromoTitleM.className)}>
						<ServerLabel boldProps={{ className: textPromoTitleL.className }}>
							{motionTitleLabel}
						</ServerLabel>
					</span>
				</Marquee>
				<ButtonCtas
					ctas={ctas}
					activeLink={activeLink}
					className={cx(styles.ctas, focusStyle.imageFocus)}
					data-testid='landings.home.heroBannerPrimaryLinesLink'
				/>
				<AnimationButton titles={animationButtonTitles} />
			</MarqueeProvider>
		</div>
	)
}
